import { ChevronLeft, ChevronRight, Plus, User } from 'lucide-react';
import type React from 'react';
import type { ContactListItem } from '../../hooks/useContactsSearch';
import { cn } from '../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { UserAvatar } from '../../ui/user-avatar';
import { EmptyState } from '../empty-state';

export interface ContactsTableProps {
  items: ContactListItem[];
  isLoading: boolean;
  selectedId?: number;
  onRowClick: (contactId: number) => void;
  renderRowActions?: (contact: ContactListItem) => React.ReactNode;
  emptyLabel?: string;
  emptyDescription?: React.ReactNode;
  onClearFilters?: () => void;
  onCreateContact?: () => void;
  pageStart: number;
  pageEnd: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onNextPage: () => void;
  onPrevPage: () => void;
}

function primaryEmail(contact: ContactListItem) {
  return contact.emails?.find((e) => e.isPrimary) ?? contact.emails?.[0];
}

function primaryPhone(contact: ContactListItem) {
  return contact.phones?.find((p) => p.isPrimary) ?? contact.phones?.[0];
}

export function ContactsTable({
  items,
  isLoading,
  selectedId,
  onRowClick,
  renderRowActions,
  emptyLabel = 'No contacts found',
  emptyDescription,
  onClearFilters,
  onCreateContact,
  pageStart,
  pageEnd,
  totalCount,
  hasNextPage,
  hasPrevPage,
  onNextPage,
  onPrevPage,
}: ContactsTableProps) {
  return (
    <div className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-[260px]">Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Created</TableHead>
            {renderRowActions && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array(5)
              .fill(0)
              .map((_, i) => (
                <TableRow key={i}>
                  {Array(renderRowActions ? 8 : 7)
                    .fill(0)
                    .map((__, j) => (
                      <TableCell key={j}>
                        <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                      </TableCell>
                    ))}
                </TableRow>
              ))
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={renderRowActions ? 8 : 7} className="h-72 p-0 text-center">
                <EmptyState
                  icon={User}
                  title={emptyLabel}
                  description={emptyDescription}
                  action={
                    onClearFilters
                      ? {
                          label: 'Clear Filters',
                          variant: 'outline',
                          onClick: onClearFilters,
                        }
                      : onCreateContact
                        ? {
                            label: 'Create Contact',
                            icon: Plus,
                            onClick: onCreateContact,
                          }
                        : undefined
                  }
                  className="py-12"
                />
              </TableCell>
            </TableRow>
          ) : (
            items.map((contact) => {
              const email = primaryEmail(contact);
              const phone = primaryPhone(contact);
              return (
                <TableRow
                  key={contact.id}
                  className={cn(
                    'group transition-colors cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-900/50',
                    selectedId === contact.id && 'bg-slate-100/50 dark:bg-slate-900/80'
                  )}
                  onClick={() => onRowClick(contact.id)}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2.5">
                      <UserAvatar
                        user={{
                          fullName: contact.displayName || contact.label,
                          avatarUrl: contact.avatarUrl,
                        }}
                        name={contact.displayName || contact.label}
                        size="sm"
                      />
                      <span className="truncate">{contact.displayName || contact.label}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-normal capitalize">
                      {contact.contactType?.label || 'General'}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    {contact.owner ? (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <UserAvatar user={contact.owner} size="xs" />
                        <span className="text-xs text-muted-foreground truncate">
                          {contact.owner.fullName || contact.owner.email}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground block">—</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <span className="text-xs text-muted-foreground truncate block">
                      {contact.companyName || '—'}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[180px]">
                    <span className="text-xs text-muted-foreground truncate block">
                      {email?.email || '—'}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[140px]">
                    <span className="text-xs text-muted-foreground truncate block">
                      {phone?.phoneRaw || phone?.phone || '—'}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(contact.createdAt * 1000).toLocaleDateString()}
                  </TableCell>
                  {renderRowActions && (
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {renderRowActions(contact)}
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {!isLoading && totalCount > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
          <span className="text-xs text-muted-foreground">
            {pageStart}–{pageEnd} of {totalCount}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!hasPrevPage}
              onClick={onPrevPage}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!hasNextPage}
              onClick={onNextPage}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
